import Checkbox from "@app/components/Checkbox";
import { NULL_TOKEN } from "@app/shared/utils/NULL_TOKEN";
import { usersAtom } from "@app/store/userStore";
import { Avatar } from "@design-system/DataDisplay/Avatar";
import { Icon } from "@design-system/Icon";
import { type AssignedAvatar } from "@design-system/Inputs/AssignButton";
import { Button } from "@design-system/Inputs/Button";
import { MultiSelect } from "@design-system/Inputs/MultiSelect";
import { Dropdown } from "@design-system/Inputs/UniversalDropdown";
import { Box } from "@design-system/Layout/Box";
import { FlexRow } from "@design-system/Layout/Flex";
import { HeaderFilter } from "@design-system/Table";
import { Text } from "@design-system/Typography/Text";
import { cn, getRandomBgColorForWhiteText } from "@design-system/Utilities";
import { useAtom } from "jotai";
import {
    type FC,
    type PropsWithChildren,
    type ReactNode,
    useMemo,
} from "react";
import { useTranslation } from "react-i18next";
import "./i18n";

import SearchBar from "@design-system/Inputs/SearchBar";

export const UserMultiSelect: FC<{
    isFilter?: boolean;
    filterLabel?: string;
    value?: (string | null)[];
    onValueChange: (val: (string | null)[]) => void;
    onReset?: () => void;
    searchFilter?: string;
    onSearchFilterChange?: (val: string | undefined) => void;
    includeNone?: boolean;
    trigger?: ReactNode;
}> = ({
    isFilter,
    filterLabel,
    value,
    onValueChange,
    onReset,
    searchFilter,
    onSearchFilterChange,
    includeNone,
    trigger,
}) => {
    const [{ data, isPending }] = useAtom(usersAtom);

    const { t } = useTranslation("UserMultiSelect");

    const usersMap = useMemo(() => {
        if (isPending || !data?.users) return {};
        return data?.users.reduce(
            (acc, { id, firstName, lastName, avatar }) => {
                acc[id] = {
                    id,
                    label: `${firstName} ${lastName}`,
                    color: getRandomBgColorForWhiteText(),
                    imgUrl: avatar?.signedUrl,
                };
                return acc;
            },
            {} as Record<string, AssignedAvatar>,
        );
    }, [data]);

    const filteredUsers = useMemo(() => {
        return (
            data?.users.filter(
                ({ firstName, lastName, email }) =>
                    !searchFilter ||
                    [firstName, lastName, email].some(
                        (s) =>
                            s &&
                            s
                                .toLowerCase()
                                .includes(searchFilter.toLowerCase()),
                    ),
            ) || []
        );
    }, [data, searchFilter]);

    const options = useMemo(() => {
        return [
            ...Object.values(usersMap).map(({ id, label }) => ({
                id,
                name: label,
            })),
            ...(includeNone ? [{ id: NULL_TOKEN, name: t("no_user") }] : []),
        ];
    }, [usersMap, includeNone]);

    /**
     * Dropdown handles string[] values, so we need to convert the (string | null)[] value to a string[]
     */
    const stringOnlyValue = useMemo(() => {
        return value?.map((v) => v ?? NULL_TOKEN) ?? [];
    }, [value]);

    /**
     * Convert the string[] value to a (string | null)[] value
     */
    const handleValueChange = (val: string[]) => {
        onValueChange(val.map((v) => (v === NULL_TOKEN ? null : v)));
    };

    return (
        <Dropdown
            value={stringOnlyValue}
            onValueChange={handleValueChange}
            onReset={onReset}
            selectType="multi"
            closeOnValueChange={false}
        >
            {(trigger ?? isFilter) ? (
                <Dropdown.ChipFilterTrigger
                    label={filterLabel ?? t("placeholder")}
                    options={options}
                />
            ) : (
                <Dropdown.Trigger />
            )}
            <Dropdown.Content align="start" hasPortal>
                {onSearchFilterChange && (
                    <SearchBar
                        searchString={searchFilter || ""}
                        setSearchString={onSearchFilterChange}
                        placeholder={t("searchbar_placeholder")}
                    />
                )}
                <Dropdown.Reset label={t("reset_label")} />
                {includeNone && (
                    <Dropdown.SelectableItem value={NULL_TOKEN}>
                        <Dropdown.Checkbox />
                        <Dropdown.ItemText>{t("no_user")}</Dropdown.ItemText>
                    </Dropdown.SelectableItem>
                )}
                {filteredUsers.map((user, idx) => (
                    <Dropdown.SelectableItem key={idx} value={user.id}>
                        <Dropdown.Checkbox />
                        <Avatar
                            className="min-w-6"
                            letter={user.firstName[0]}
                            imageUrl={usersMap[user.id].imgUrl}
                            hexColor={usersMap[user.id].color}
                            variant="circle"
                        />
                        <Dropdown.ItemText>
                            {usersMap[user.id].label}
                        </Dropdown.ItemText>
                    </Dropdown.SelectableItem>
                ))}
            </Dropdown.Content>
        </Dropdown>
    );
};

/**
 * @deprecated Use UserMultiSelect instead
 */
const LegacyUserMultiSelect: FC<
    PropsWithChildren<{
        isHeader?: boolean;
        values: (string | null)[] | undefined;
        onValuesChange: (val: (string | null)[]) => void;
        searchFilter?: string;
        onSearchFilterChange?: (val: string | undefined) => void;
        includeNone?: boolean;
    }>
> = ({
    isHeader,
    values,
    onValuesChange,
    searchFilter,
    onSearchFilterChange,
    includeNone,
}) => {
    const [{ data, isPending }] = useAtom(usersAtom);

    const { t } = useTranslation("UserMultiSelect");

    const usersMap = useMemo(() => {
        if (isPending || !data?.users) return {};
        return data?.users
            .filter(
                ({ firstName, lastName, email }) =>
                    !searchFilter ||
                    [firstName, lastName, email].some(
                        (s) =>
                            s &&
                            s
                                .toLowerCase()
                                .includes(searchFilter.toLowerCase()),
                    ),
            )
            .reduce(
                (acc, { id, firstName, lastName, avatar }) => {
                    acc[id] = {
                        id,
                        label: `${firstName} ${lastName}`,
                        color: getRandomBgColorForWhiteText(),
                        imgUrl: avatar?.signedUrl,
                    };
                    return acc;
                },
                {} as Record<string, AssignedAvatar>,
            );
    }, [data]);

    const filteredUsers = useMemo(() => {
        return (
            data?.users.filter(
                ({ firstName, lastName, email }) =>
                    !searchFilter ||
                    [firstName, lastName, email].some(
                        (s) =>
                            s &&
                            s
                                .toLowerCase()
                                .includes(searchFilter.toLowerCase()),
                    ),
            ) || []
        );
    }, [data, searchFilter]);

    const active = !!values?.length;

    return (
        <MultiSelect values={values || []} onValuesChange={onValuesChange}>
            <MultiSelect.Trigger>
                {isHeader ? (
                    <HeaderFilter active={active}>
                        {t("placeholder", {
                            context: active && "active",
                            count: values?.length,
                        })}
                    </HeaderFilter>
                ) : (
                    <Button active={active} variant="outlined" size="sm">
                        {t("placeholder")}
                        <Icon name="chevronDown" />
                    </Button>
                )}
            </MultiSelect.Trigger>
            <MultiSelect.Content>
                {onSearchFilterChange && (
                    <MultiSelect.Search
                        searchString={searchFilter || ""}
                        setSearchString={onSearchFilterChange}
                        placeholder={t("searchbar_placeholder")}
                    />
                )}
                <MultiSelect.Reset label={t("reset_label")} />
                {includeNone && (
                    <MultiSelect.Item value={null}>
                        {({ isSelected }) => (
                            <FlexRow
                                alignItems="center"
                                alignSelf="stretch"
                                gap="3"
                                br="lg"
                                className={cn("flex-1 ")}
                            >
                                <Box alignItems="center">
                                    <Checkbox checked={isSelected} />
                                </Box>
                                <Text className={cn("group-hover:text-green")}>
                                    {t("no_user")}
                                </Text>
                            </FlexRow>
                        )}
                    </MultiSelect.Item>
                )}
                {filteredUsers.map((user, idx) => (
                    <MultiSelect.Item key={idx} value={user.id}>
                        {({ isSelected }) => (
                            <FlexRow
                                alignItems="center"
                                alignSelf="stretch"
                                gap="3"
                                br="lg"
                                className={cn("flex-1")}
                            >
                                <Box alignItems="center">
                                    <Checkbox checked={isSelected} />
                                </Box>
                                <FlexRow
                                    gap="2"
                                    justifyContent="between"
                                    w="full"
                                >
                                    <Text>{usersMap[user.id].label}</Text>
                                    <Avatar
                                        letter={user.firstName[0]}
                                        imageUrl={usersMap[user.id].imgUrl}
                                        hexColor={usersMap[user.id].color}
                                        variant="circle"
                                    />
                                </FlexRow>
                            </FlexRow>
                        )}
                    </MultiSelect.Item>
                ))}
            </MultiSelect.Content>
        </MultiSelect>
    );
};

export default LegacyUserMultiSelect;
